module PortalLibs.Client.Components.Dropdown


open Fable.Core
open FSharpPlus
open Fable.Core.JsInterop
open Fable.React
open Fable.React.Props
open Fulma
open Elmish.React
open Fable.FontAwesome
open PortalLibs.Client.Util
open PortalLibs.Shared
open Feliz
type ValueWrapper<'T> = { Value: 'T }

[<Import("default", from = "react-virtualized-select")>]
let ReactSelect: obj = jsNative

importAll "react-select/dist/react-select.css"

importAll "react-virtualized-select/styles.css"

[<Import("", from = "react-select-fast-filter-options")>]
let ReactSelectFastFilterOptions: obj = jsNative

let createFilterOptions (_options: obj) =
  importDefault "react-select-fast-filter-options"

let calcDropdownWidth set display =
  calcMaxDisplayWidth 226.0 display 9.7 "" set

let calcDropdownWidthWithPrint printView set display =
  if printView then
    calcMaxDisplayWidth 226.0 display 6.5 "" set
  else
    calcDropdownWidth set display

type OptionItem<'T> =
  { label: string
    value: string
    orig: 'T }

type DropdownType =
  | Standard
  | Fast

type displayValue<'T> = 'T -> string

type valueChanged<'T, 'U> = 'T option -> 'U

type valueListChanged<'T, 'U> = 'T list -> 'U

type valueChangedWithSource<'T, 'U, 'Source> = 'T option * 'Source -> 'U

type UpdateModel<'T, 'U, 'Source> =
  | Simple of valueChanged<'T, 'U>
  | Source of 'Source * valueChangedWithSource<'T, 'U, 'Source>

type DropdownItems<'T> =
  | ItemList of 'T list
  | ItemListResultOption of Result<'T list, string> option
  | ItemListOption of 'T list option

type areEqual<'T, 'Id> = 'Id -> 'T -> bool

type urlForItem<'T> = 'T -> string

type SelectedItem<'T, 'Id> =
  | Item of 'T option
  | ItemId of 'Id option * areEqual<'T, 'Id>

type SingleSelect<'T, 'U, 'Source, 'Id> =
  { UpdateModel: UpdateModel<'T, 'U, 'Source>
    SelectedItem: SelectedItem<'T, 'Id>
    LinkUrl: urlForItem<'T> option }

type MultiSelect<'T, 'U, 'Id> =
  { ValueChanged: valueListChanged<'T, 'U>
    SelectedItems: 'Id list * areEqual<'T, 'Id> }

type SelectMode<'T, 'U, 'Source, 'Id> =
  | SingleSelect of SingleSelect<'T, 'U, 'Source, 'Id>
  | MultiSelect of MultiSelect<'T, 'U, 'Id>

type DropdownTitle =
  | Name of string
  | DropdownOnly


//type DropdownConfig<'T, 'U, 'Source, 'Id> =
//  { Title : DropdownTitle
//    Items : DropdownItems<'T>
//    DisplayValue : displayValue<'T>
//    SelectMode : SelectMode<'T, 'U, 'Source, 'Id>
//    Searchable : bool
//    Type : DropdownType
//    key : string }

type WrappedItem<'T> = { Text: string; OriginalItem: 'T }

let toWrappedItem displayValue item =
  { Text = displayValue item
    OriginalItem = item }

let private wrappedItemValue item =
  match item with
  | null -> None
  | x -> Some !!x?OriginalItem

let private itemList<'T, 'U, 'Source, 'Id>
  (config: {| Title: DropdownTitle
              Items: DropdownItems<'T>
              DisplayValue: displayValue<'T>
              SelectMode: SelectMode<'T, 'U, 'Source, 'Id>
              Searchable: bool
              Type: DropdownType
              Key: string |})
  =
  match config.Items with
  | ItemList list -> list
  | ItemListResultOption option -> Result.resultOrEmptyListIfNone option
  | ItemListOption option -> Option.emptyListIfNone option

let private selectedItem config selectedItem =
  match selectedItem with
  | Item i -> i
  | ItemId (idOpt, areEqual) ->
    idOpt
    >>= (fun id -> itemList config |> List.tryFind (areEqual id))


let OnlyOneDropdownControlGeneric<'T, 'U, 'Source, 'Id> =
  React.memo (
    "OnlyOneDropdownControl",
    (fun config ->
      let itemList = itemList<'T, 'U, 'Source, 'Id> config

      let options =
        itemList
        |>> (toWrappedItem config.DisplayValue)
        |> List.toArray

      let selectedValue =
        match config.SelectMode with
        | SingleSelect single ->
          box (
            selectedItem config single.SelectedItem
            |>> (toWrappedItem config.DisplayValue)
          )
        | MultiSelect multi ->
          let ids, areEqual = multi.SelectedItems

          box (
            ids
            |> List.choose (fun id -> List.tryFind (areEqual id) itemList)
            |>> toWrappedItem config.DisplayValue
            |> List.toArray
          )

      let onChange =
        match config.SelectMode with
        | SingleSelect single ->
          box
            (fun value ->
              match single.UpdateModel with
              | Simple u -> value |> wrappedItemValue |> u
              | Source (source, u) -> u (wrappedItemValue value, source))
        | MultiSelect multi ->
          box
            (fun values ->
              values
              |> List.ofArray
              |> List.choose wrappedItemValue
              |> multi.ValueChanged)

      let props =
        createObj [
          yield "options" => options
          yield "value" => selectedValue
          yield "searchable" => config.Searchable
          match config.SelectMode with
          | MultiSelect _ ->
            yield "removeSelected" => true
            yield "multi" => true
          | _ -> ()
          yield "onChange" => onChange
          yield "labelKey" => "Text"
          yield "valueKey" => "Text"
          yield "autoBlur" => true
          if config.Type = Fast then
            let filterOptions =
              createFilterOptions (
                createObj [
                  "options" => options
                  "labelKey" => "Text"
                  "valueKey" => "Text"
                ]
              )

            yield "filterOptions" => filterOptions
        ]

      Html.div [
        prop.className "form-dropdown"
        prop.children [
          match config.Type with
          | Standard -> yield ofImport "default" "react-select" props []
          | Fast -> yield ofImport "default" "react-virtualized-select" props []
        ]
      ]),
    (fun (config: {| Title: DropdownTitle
                     Items: DropdownItems<'T>
                     DisplayValue: displayValue<'T>
                     SelectMode: SelectMode<'T, 'U, 'Source, 'Id>
                     Searchable: bool
                     Type: DropdownType
                     Key: string |}) -> config.Key),
    areEqual = Fable.React.Helpers.equalsButFunctions
  )

let OnlyOneDropdownGeneric<'T, 'U, 'Source, 'Id when 'T: equality and 'Source: equality and 'Id: equality> =
  React.memo (
    "OnlyOneDropdown",
    (fun (config: {| Title: DropdownTitle
                     Items: DropdownItems<'T>
                     DisplayValue: displayValue<'T>
                     SelectMode: SelectMode<'T, 'U, 'Source, 'Id>
                     Searchable: bool
                     Type: DropdownType
                     Key: string |}) ->
      let OnlyOneDropdownControlSpecific =
        OnlyOneDropdownControlGeneric<'T, 'U, 'Source, 'Id>

      let dropdownView =
        match config.Title with
        | DropdownOnly ->
          div [ Style [ MinWidth "250px" ] ] [
            OnlyOneDropdownControlSpecific
              {| Title = config.Title
                 Items = config.Items
                 DisplayValue = config.DisplayValue
                 SelectMode = config.SelectMode
                 Searchable = config.Searchable
                 Type = config.Type
                 Key = $"%s{config.Key}-Control" |}
          ]
        | Name name ->
          field
            name
            [ Columns.columns [ Columns.IsVCentered ] [
                yield
                  Column.column [] [
                    OnlyOneDropdownControlSpecific
                      {| Title = config.Title
                         Items = config.Items
                         DisplayValue = config.DisplayValue
                         SelectMode = config.SelectMode
                         Searchable = config.Searchable
                         Type = config.Type
                         Key = $"%s{config.Key}-Control" |}
                  ]
                match config.SelectMode with
                | MultiSelect _ -> ()
                | SingleSelect single ->
                  match single.LinkUrl, selectedItem config single.SelectedItem with
                  | Some linkUrl, Some item ->
                    yield
                      Column.column [ Column.Width(Screen.Tablet, Column.IsNarrow) ] [
                        a [ OnClick goToUrl

                            Href(linkUrl item) ] [
                          str "View"
                        ]
                      ]
                  | _ -> ()
              ] ]

      let dropdownLazyView =
        let lazyView selected =
          match config.SelectMode with
          | SingleSelect select ->
            match select.UpdateModel with
            | Simple _ -> lazyView (fun _ -> dropdownView) (config.Items, selected)
            | Source (source, _) -> lazyView (fun _ -> dropdownView) (config.Items, selected, source)
          | MultiSelect _ -> lazyView (fun _ -> dropdownView) (config.Items, selected)

        match config.SelectMode with
        | SingleSelect single ->
          match single.SelectedItem with
          | Item i -> lazyView i
          | ItemId (id, _) -> lazyView id
        | MultiSelect multi ->
          let ids, _ = multi.SelectedItems
          lazyView ids

      let textValueWithSpinner txt =
        let dropdownCtrl =
          Control.div [ Control.HasIconLeft
                        Control.Props [
                          Style [ MinWidth "250px" ]
                        ] ] [
            Input.text [
              Input.IsReadOnly true
              Input.Value txt
            ]
            Icon.icon [ Icon.Size IsSmall
                        Icon.IsLeft
                        Icon.Props [ Style [ Color "#4a4a4a" ] ] ] [
              Fa.i [ Fa.Solid.Spinner; Fa.Pulse ] []
            ]
          ]

        match config.Title with
        | DropdownOnly -> dropdownCtrl
        | Name name ->
          div [ ClassName "report-search-text-field" ] [
            field
              name
              [ Columns.columns [ Columns.IsVCentered ] [
                  Column.column [] [ dropdownCtrl ]
                ] ]
          ]

      let truncatedTextValue (txt: string) =
        let truncatedTxt =
          if txt.Length > 30 then
            $"%s{txt.Substring(0, 30)}..."
          else
            txt
        let dropdownCtrl =
          Html.div
            [ Bulma.tooltip.text txt
              prop.style [ style.minWidth 250 ]
              prop.children [
                input [
                  ClassName "input error-input"
                  ReadOnly true
                  Value truncatedTxt
                ] ]
          ]

        match config.Title with
        | DropdownOnly -> dropdownCtrl
        | Name name ->
          div [ ClassName "report-search-text-field" ] [
            field
              name
              [ Columns.columns [ Columns.IsVCentered ] [
                  Column.column [] [ dropdownCtrl ]
                ] ]
          ]

      let resultLoadingView itemsOpt =
        match itemsOpt with
        | None -> textValueWithSpinner "Loading..."
        | Some itemsRes ->
          match itemsRes with
          | Error msg -> truncatedTextValue msg
          | Ok _ -> dropdownLazyView

      let optionLoadingView itemsOpt =
        match itemsOpt with
        | None -> textValueWithSpinner "Loading..."
        | Some _ -> dropdownLazyView

      match config.Items with
      | ItemList _ -> dropdownLazyView
      | ItemListResultOption opt -> resultLoadingView opt
      | ItemListOption opt -> optionLoadingView opt),
    (fun (config: {| Title: DropdownTitle
                     Items: DropdownItems<'T>
                     DisplayValue: displayValue<'T>
                     SelectMode: SelectMode<'T, 'U, 'Source, 'Id>
                     Searchable: bool
                     Type: DropdownType
                     Key: string |}) -> config.Key),
    areEqual = Fable.React.Helpers.equalsButFunctions
  )
